import * as React from "react";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
} from "@material-ui/core";
import { IconType } from "Common/enum";
import TypographySubtitle from "Components/Typography/typography-subtitle";
import { Form, Formik } from "formik";
import ClientAppApi from "Services/Api/client-app-api";
import { AxiosError } from "axios";
import ProgressSpinner from "Components/ProgressSpinner/progress-spinner";
import ErrorDialog from "Components/Dialogs/ErrorDialog/error-dialog";
import Swal from "sweetalert2";
import TextFieldEntered from "Components/Inputs/TextFieldEntered/text-field-entered";
import ButtonAction from "Components/Buttons/ButtonAction/button-action";
import ButtonSubaction from "Components/Buttons/ButtonSubaction/button-subaction";
import ButtonIcon from "Components/Buttons/button-icon";
import SvgEmailEnvelope from "Components/Images/image-email-envelope";
import {
  IEmailAddressFormFields,
  IMobileAppSendDownloadEmailPopupProps,
} from "./interfaces-mobile-app-send-download-email-popup";
import { Add } from "@material-ui/icons";
import { stylesMobileAppSendDownloadEmailPopup } from "./styles";

const EMAIL_ADDRESS_MAX_LENGTH: number = 100;

const clientAppApi: ClientAppApi = new ClientAppApi();

export default function MobileAppSendDownloadEmailPopup(
  props: IMobileAppSendDownloadEmailPopupProps
) {
  const customStyles = stylesMobileAppSendDownloadEmailPopup();
  const [isOpen, setIsOpen] = React.useState(false);
  const [errorResponse, setErrorResponse] = React.useState<AxiosError | null>(
    null
  );
  const [isBlockedUi, setIsBlockedUi] = React.useState(false);
  const [emailAddressList, setEmailAddressList] = React.useState<string[]>([]);

  const handleOpen = async (
    event: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    setEmailAddressList([]);
    setIsOpen(true);
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setIsOpen(false);
  };

  const handleSendButtonClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    setIsBlockedUi(true);
    await clientAppApi
      .sendMobileAppDownloadEmail({
        iaMobileAppVersionID: props.iaMobileAppVersionID,
        emailAddresses: emailAddressList,
      })
      .then(async () => {
        Swal.fire({
          icon: "success",
          text: "Successfully sent!",
          width: "25rem",
          customClass: {
            title: "swal-title",
            container: "swal-container",
            content: "swal-content",
            confirmButton: "swal-confirm-button",
          },
        });
      })
      .then(() => setIsOpen(false))
      .catch((error) => {
        console.error(error);
        setErrorResponse(error);
      })
      .finally(() => {
        setIsBlockedUi(false);
      });
  };

  const handleErrorDialogOkClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setErrorResponse(null);
  };

  const validationShape = {
    emailAddress: Yup.string().email("Invalid email address").notRequired(),
  };

  return (
    <>
      <ButtonIcon
        iconType={IconType.SvgImageIcon}
        icon={<SvgEmailEnvelope />}
        handleClick={handleOpen}
        height="30px"
        width="30px"
      />
      <Dialog
        open={isOpen}
        maxWidth={"sm"}
        onClose={handleClose}
        style={{ zIndex: 500 }}
      >
        <ProgressSpinner isDisplayed={isBlockedUi}>
          <DialogTitle className={customStyles.dialogTitle}>
            <Typography className={customStyles.titleLine}>
              Send email with link to upload
            </Typography>
            <Typography className={customStyles.titleLine}>
              {`${props.appName} release ${props.versionNo}`}
            </Typography>
          </DialogTitle>
          <Formik
            enableReinitialize={!isBlockedUi}
            initialValues={{ emailAddress: "" } as IEmailAddressFormFields}
            onSubmit={(props: IEmailAddressFormFields) => {}} // Use ButtonIcon handleClick below.
            validationSchema={Yup.object().shape(validationShape)}
          >
            {(formProps) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
              } = formProps;
              return (
                <Form>
                  <DialogContent dividers>
                    <Grid container alignItems="flex-start" direction="column">
                      <Grid
                        container
                        item
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Grid item sm={11}>
                          <TextFieldEntered
                            id="emailAddress"
                            name="emailAddress"
                            label="Email"
                            value={values.emailAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              !!(errors.emailAddress && touched.emailAddress)
                            }
                            helperText={
                              errors.emailAddress &&
                              touched.emailAddress &&
                              errors.emailAddress
                            }
                            inputProps={{
                              maxLength: EMAIL_ADDRESS_MAX_LENGTH,
                            }}
                          />
                        </Grid>
                        <Grid item sm={1}>
                          <ButtonIcon
                            iconType={IconType.SvgImageIcon}
                            icon={<Add fontSize="large" />}
                            handleClick={() => {
                              if (
                                !!values.emailAddress &&
                                !errors.emailAddress &&
                                !emailAddressList.some(
                                  (e) =>
                                    e.trim().toLocaleLowerCase() ===
                                    values.emailAddress
                                      .trim()
                                      .toLocaleLowerCase()
                                )
                              ) {
                                setEmailAddressList([
                                  ...emailAddressList,
                                  values.emailAddress,
                                ]);
                                setFieldValue("emailAddress", "");
                              }
                            }}
                            height="25px"
                            width="25px"
                          />
                        </Grid>
                      </Grid>
                      <Grid container item direction="column">
                        {emailAddressList.map((email) => (
                          <Typography
                            key={email}
                            className={customStyles.emailAddressListItem}
                          >
                            {email}
                          </Typography>
                        ))}
                      </Grid>
                    </Grid>
                  </DialogContent>
                </Form>
              );
            }}
          </Formik>
          <DialogActions className={customStyles.dialogActions}>
            <ButtonSubaction text="Cancel" handleClick={handleClose} />
            <ButtonAction
              text="Send"
              isExecuting={isBlockedUi}
              handleClick={handleSendButtonClick}
              disabled={emailAddressList.length === 0}
            />
          </DialogActions>
        </ProgressSpinner>
      </Dialog>
      <ErrorDialog
        isOpen={!!errorResponse}
        error={errorResponse}
        handleOkClick={handleErrorDialogOkClick}
      />
    </>
  );
}
